import React, { useState } from 'react';
import Layout from '../components/Layout';
import AddressBook from '../components/AddressBook';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { 
  FaLock, FaTrash, FaCheckCircle, FaTimesCircle, FaIdCard, 
  FaUniversity, FaFileInvoice, FaCar, FaMoneyCheckAlt, FaCamera,
  FaMapMarkedAlt, FaShieldAlt
} from 'react-icons/fa';
import './AccountSettings.css';

const AccountSettings = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'security' or 'addresses'
  
  // Profile Update
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: ''
  });

  // Initialize profile data from user
  React.useEffect(() => {
    if (user) {
      const firstName = user.consumerProfile?.firstName || '';
      const lastName = user.consumerProfile?.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      setProfileData({
        name: fullName,
        email: user.email || '',
        phone: user.phone || '',
        businessName: user.businessName || ''
      });
    }
  }, [user]);
  
  // Password Change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Verification States
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [gstinNumber, setGstinNumber] = useState('');
  const [upiId, setUpiId] = useState('');
  const [licenseData, setLicenseData] = useState({
    licenseNumber: '',
    dateOfBirth: ''
  });
  const [profilePicUrl, setProfilePicUrl] = useState('');
  
  // Bank Details
  const [bankData, setBankData] = useState({
    accountNumber: '',
    ifsc: '',
    accountHolderName: '',
    upiId: ''
  });
  const [showBankModal, setShowBankModal] = useState(false);
  
  // Delete Account
  const [deleteData, setDeleteData] = useState({
    password: '',
    confirmation: ''
  });

  const [loading, setLoading] = useState(false);

  // Handle Profile Update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.put('/users/profile', profileData);
      toast.success(response.data.message || 'Profile updated successfully');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle Password Change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Handle Aadhaar Verification
  const handleAadhaarVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/users/verify-aadhaar', { aadhaarNumber });
      toast.success('Aadhaar verified successfully!');
      setAadhaarNumber('');
      // Refresh user data to show updated verification status
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Aadhaar verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle PAN Verification
  const handlePANVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/users/verify-pan', { panNumber });
      toast.success('PAN verified successfully!');
      setPanNumber('');
      // Refresh user data to show updated verification status
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'PAN verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle GST Verification
  const handleGSTVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/users/verify-gst', { gstin: gstinNumber });
      toast.success('GST verified successfully');
      if (response.data.data) {
        toast.info(`Business: ${response.data.data.businessName}`);
      }
      setGstinNumber('');
      // Refresh user data to show updated verification status
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'GST verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle UPI Verification
  const handleUPIVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/users/verify-upi', { upiId });
      toast.success('UPI ID verified successfully!');
      setUpiId('');
      // Refresh user data to show updated verification status
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'UPI verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Driving License Verification
  const handleLicenseVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/users/verify-driving-license', licenseData);
      toast.success('Driving License verified successfully!');
      setLicenseData({ licenseNumber: '', dateOfBirth: '' });
      // Refresh user data to show updated verification status
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'License verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Profile Picture Upload
  const handleProfilePictureUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/users/upload-profile-picture', { imageUrl: profilePicUrl });
      toast.success('Profile picture updated successfully!');
      setProfilePicUrl('');
      // Refresh user data to show updated profile picture
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload profile picture');
    } finally {
      setLoading(false);
    }
  };

  // Handle Bank Verification
  const handleVerifyBank = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/users/verify-bank', bankData);
      toast.success('Bank account verified successfully!');
      setShowBankModal(false);
      setBankData({ accountNumber: '', ifsc: '', accountHolderName: '', upiId: '' });
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bank verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBankInputChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({ ...prev, [name]: value }));
  };

  // Handle Account Deletion
  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (deleteData.confirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!window.confirm('Are you absolutely sure? This action cannot be undone!')) {
      return;
    }

    setLoading(true);
    try {
      await api.delete('/auth/delete-account', { data: deleteData });
      toast.success('Account deleted successfully');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="account-settings-page">
        <div className="page-header">
          <h1>Account Settings</h1>
          <p>Manage your security, verification and address settings</p>
        </div>

        {/* Tabs Navigation */}
        <div className="settings-tabs">
          <button 
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <FaIdCard /> Profile Information
          </button>
          <button 
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <FaShieldAlt /> Security & Verification
          </button>
          <button 
            className={`tab-btn ${activeTab === 'addresses' ? 'active' : ''}`}
            onClick={() => setActiveTab('addresses')}
          >
            <FaMapMarkedAlt /> Address Book
          </button>
        </div>

        {/* Profile Information Tab */}
        {activeTab === 'profile' && (
          <div className="settings-grid">
            <div className="settings-card">
              <div className="card-header">
                <FaIdCard />
                <h2>Personal Information</h2>
              </div>
              <form onSubmit={handleProfileUpdate}>
                {/* Name Field */}
                <div className="form-group">
                  <label>Name</label>
                  <div className="input-with-badge">
                    <input
                      type="text"
                      className="form-control"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      placeholder="Full name"
                    />
                    {user?.consumerProfile?.verified ? (
                      <span className="badge badge-verified">
                        <FaCheckCircle /> Verified
                      </span>
                    ) : user?.consumerProfile?.verificationStatus === 'pending' ? (
                      <span className="badge badge-pending">
                        ⏳ Pending Verification
                      </span>
                    ) : null}
                  </div>
                  <small className="form-hint">
                    {!user?.consumerProfile?.verified && 'Changes will be marked for admin verification'}
                  </small>
                </div>

                {/* Email Field */}
                <div className="form-group">
                  <label>Email</label>
                  <div className="input-with-badge">
                    <input
                      type="email"
                      className="form-control"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    />
                    {user?.emailVerified ? (
                      <span className="badge badge-verified">
                        <FaCheckCircle /> Verified
                      </span>
                    ) : (
                      <span className="badge badge-unverified">
                        <FaTimesCircle /> Not Verified
                      </span>
                    )}
                  </div>
                  <small className="form-hint">
                    Email verification helps secure your account
                  </small>
                </div>

                {/* Phone Field */}
                <div className="form-group">
                  <label>Phone Number</label>
                  <div className="input-with-badge">
                    <input
                      type="tel"
                      className="form-control"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    />
                    {user?.phoneVerified ? (
                      <span className="badge badge-verified">
                        <FaCheckCircle /> Verified
                      </span>
                    ) : (
                      <span className="badge badge-unverified">
                        <FaTimesCircle /> Not Verified
                      </span>
                    )}
                  </div>
                </div>

                {/* Business Name for Sellers */}
                {user?.userType === 'seller' && (
                  <div className="form-group">
                    <label>Business Name</label>
                    <div className="input-with-badge">
                      <input
                        type="text"
                        className="form-control"
                        value={profileData.businessName}
                        onChange={(e) => setProfileData({ ...profileData, businessName: e.target.value })}
                      />
                      {user?.gstVerification?.verified ? (
                        <span className="badge badge-verified">
                          <FaCheckCircle /> Verified via GST
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
              </form>
            </div>

            {/* Bank Details Card */}
            {user?.userType === 'seller' && (
              <div className="settings-card">
                <div className="card-header">
                  <FaUniversity />
                  <h2>Bank Details</h2>
                  {user?.bankDetails?.verified && (
                    <span className="verified-badge"><FaCheckCircle /> Verified</span>
                  )}
                </div>
                {user?.bankDetails?.verified ? (
                  <div className="verified-info">
                    <p><strong>Account Holder:</strong> {user.bankDetails.accountHolderName}</p>
                    <p><strong>Account Number:</strong> ****{user.bankDetails.accountNumber?.slice(-4)}</p>
                    <p><strong>IFSC:</strong> {user.bankDetails.ifsc}</p>
                    {user.bankDetails.upiId && (
                      <p><strong>UPI ID:</strong> {user.bankDetails.upiId}</p>
                    )}
                    <button 
                      className="btn btn-outline" 
                      onClick={() => setShowBankModal(true)}
                      style={{ marginTop: '12px' }}
                    >
                      Update Bank Details
                    </button>
                  </div>
                ) : (
                  <div className="pending-info">
                    <p className="info-text">Complete bank verification to receive payments directly</p>
                    <button className="btn btn-primary" onClick={() => setShowBankModal(true)}>
                      Verify Bank Account
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Verification Status Card */}
            <div className="settings-card">
              <div className="card-header">
                <FaShieldAlt />
                <h2>Verification Status</h2>
              </div>
              <div className="verification-status">
                <div className={`status-item ${user?.isVerified ? 'verified' : 'pending'}`}>
                  <FaCheckCircle />
                  <span>Account Verified</span>
                  {user?.isVerified && <span className="badge badge-success">✓</span>}
                </div>
                {user?.emailVerified !== undefined && (
                  <div className={`status-item ${user?.emailVerified ? 'verified' : 'pending'}`}>
                    <FaCheckCircle />
                    <span>Email Verified</span>
                    {user?.emailVerified && <span className="badge badge-success">✓</span>}
                  </div>
                )}
                {user?.phoneVerified !== undefined && (
                  <div className={`status-item ${user?.phoneVerified ? 'verified' : 'pending'}`}>
                    <FaCheckCircle />
                    <span>Phone Verified</span>
                    {user?.phoneVerified && <span className="badge badge-success">✓</span>}
                  </div>
                )}
                {user?.userType === 'seller' && (
                  <>
                    <div className={`status-item ${user?.bankDetails?.verified ? 'verified' : 'pending'}`}>
                      <FaUniversity />
                      <span>Bank Verified</span>
                      {user?.bankDetails?.verified && <span className="badge badge-success">✓</span>}
                    </div>
                    <div className={`status-item ${user?.gstVerification?.verified ? 'verified' : 'pending'}`}>
                      <FaIdCard />
                      <span>GST Verified</span>
                      {user?.gstVerification?.verified && <span className="badge badge-success">✓</span>}
                    </div>
                  </>
                )}
                {(user?.govtIdVerification?.aadhaar?.verified || user?.govtIdVerification?.pan?.verified) && (
                  <>
                    {user?.govtIdVerification?.aadhaar?.verified && (
                      <div className="status-item verified">
                        <FaIdCard />
                        <span>Aadhaar Verified</span>
                        <span className="badge badge-success">✓</span>
                      </div>
                    )}
                    {user?.govtIdVerification?.pan?.verified && (
                      <div className="status-item verified">
                        <FaFileInvoice />
                        <span>PAN Verified</span>
                        <span className="badge badge-success">✓</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bank Modal */}
        {showBankModal && (
          <div className="modal-overlay" onClick={() => setShowBankModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Verify Bank Account</h2>
                <button className="close-btn" onClick={() => setShowBankModal(false)}>×</button>
              </div>
              <form onSubmit={handleVerifyBank}>
                <div className="form-group">
                  <label>Account Holder Name *</label>
                  <input
                    type="text"
                    name="accountHolderName"
                    className="form-control"
                    value={bankData.accountHolderName}
                    onChange={handleBankInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Account Number *</label>
                  <input
                    type="text"
                    name="accountNumber"
                    className="form-control"
                    value={bankData.accountNumber}
                    onChange={handleBankInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>IFSC Code *</label>
                  <input
                    type="text"
                    name="ifsc"
                    className="form-control"
                    value={bankData.ifsc}
                    onChange={handleBankInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>UPI ID (Optional)</label>
                  <input
                    type="text"
                    name="upiId"
                    className="form-control"
                    placeholder="yourname@upi"
                    value={bankData.upiId}
                    onChange={handleBankInputChange}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowBankModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Security & Verification Tab */}
        {activeTab === 'security' && (
        <div className="settings-grid">
          {/* Change Password */}
          <div className="settings-card">
            <div className="card-header">
              <FaLock />
              <h2>Change Password</h2>
            </div>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  minLength="6"
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                Change Password
              </button>
            </form>
          </div>

          {/* Profile Picture */}
          <div className="settings-card">
            <div className="card-header">
              <FaCamera />
              <h2>Profile Picture</h2>
            </div>
            {user?.profileImage && (
              <div className="current-profile-pic">
                <img src={user.profileImage} alt="Profile" />
              </div>
            )}
            <form onSubmit={handleProfilePictureUpload}>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="url"
                  className="form-control"
                  value={profilePicUrl}
                  onChange={(e) => setProfilePicUrl(e.target.value)}
                  placeholder="Enter image URL"
                  required
                />
                <small>In production, this would be a file upload</small>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                Upload Picture
              </button>
            </form>
          </div>

          {/* Aadhaar Verification */}
          <div className="settings-card">
            <div className="card-header">
              <FaIdCard />
              <h2>Aadhaar Verification</h2>
              {user?.govtIdVerification?.aadhaar?.verified && (
                <span className="verified-badge"><FaCheckCircle /> Verified</span>
              )}
              {user?.govtIdVerification?.aadhaar?.pending && !user?.govtIdVerification?.aadhaar?.verified && (
                <span className="pending-badge"><FaTimesCircle /> Pending</span>
              )}
            </div>
            {user?.govtIdVerification?.aadhaar?.verified ? (
              <div className="verified-info">
                <p>Aadhaar Number: ****-****-{user.govtIdVerification.aadhaar.number}</p>
                <p className="verified-date">
                  Verified on {new Date(user.govtIdVerification.aadhaar.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            ) : user?.govtIdVerification?.aadhaar?.pending ? (
              <div className="pending-info">
                <p>Aadhaar Number: ****-****-{user.govtIdVerification.aadhaar.number}</p>
                <p className="pending-date">
                  Submitted on {new Date(user.govtIdVerification.aadhaar.submittedAt).toLocaleDateString()}
                </p>
                <p className="info-text">Your Aadhaar verification is under review. This usually takes 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleAadhaarVerification}>
                <div className="form-group">
                  <label>Aadhaar Number (12 digits)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={aadhaarNumber}
                    onChange={(e) => setAadhaarNumber(e.target.value)}
                    placeholder="Enter 12-digit Aadhaar number"
                    pattern="[2-9]{1}[0-9]{11}"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Verify Aadhaar
                </button>
              </form>
            )}
          </div>

          {/* PAN Verification */}
          <div className="settings-card">
            <div className="card-header">
              <FaFileInvoice />
              <h2>PAN Verification</h2>
              {user?.govtIdVerification?.pan?.verified && (
                <span className="verified-badge"><FaCheckCircle /> Verified</span>
              )}
              {user?.govtIdVerification?.pan?.pending && !user?.govtIdVerification?.pan?.verified && (
                <span className="pending-badge"><FaTimesCircle /> Pending</span>
              )}
            </div>
            {user?.govtIdVerification?.pan?.verified ? (
              <div className="verified-info">
                <p>PAN Number: {user.govtIdVerification.pan.number}</p>
                <p className="verified-date">
                  Verified on {new Date(user.govtIdVerification.pan.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            ) : user?.govtIdVerification?.pan?.pending ? (
              <div className="pending-info">
                <p>PAN Number: {user.govtIdVerification.pan.number}</p>
                <p className="pending-date">
                  Submitted on {new Date(user.govtIdVerification.pan.submittedAt).toLocaleDateString()}
                </p>
                <p className="info-text">Your PAN verification is under review. This usually takes 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handlePANVerification}>
                <div className="form-group">
                  <label>PAN Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={panNumber}
                    onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                    placeholder="ABCDE1234F"
                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                    maxLength="10"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Verify PAN
                </button>
              </form>
            )}
          </div>

          {/* GST Verification */}
          {user?.userType === 'seller' && (
            <div className="settings-card">
              <div className="card-header">
                <FaFileInvoice />
                <h2>GST Verification</h2>
                {user?.gstVerification?.verified && (
                  <span className="verified-badge"><FaCheckCircle /> Verified</span>
                )}
                {user?.gstVerification?.pending && !user?.gstVerification?.verified && (
                  <span className="pending-badge"><FaTimesCircle /> Pending</span>
                )}
              </div>
              {user?.gstVerification?.verified ? (
                <div className="verified-info">
                  <p>GSTIN: {user.gstVerification.gstin}</p>
                  <p>Business: {user.gstVerification.businessName}</p>
                  <p className="verified-date">
                    Verified on {new Date(user.gstVerification.verifiedAt).toLocaleDateString()}
                  </p>
                </div>
              ) : user?.gstVerification?.pending ? (
                <div className="pending-info">
                  <p>GSTIN: {user.gstVerification.gstin}</p>
                  <p className="pending-date">
                    Submitted on {new Date(user.gstVerification.submittedAt).toLocaleDateString()}
                  </p>
                  <p className="info-text">Your GST verification is under review. This usually takes 24-48 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleGSTVerification}>
                  <div className="form-group">
                    <label>GSTIN (15 characters)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={gstinNumber}
                      onChange={(e) => setGstinNumber(e.target.value.toUpperCase())}
                      placeholder="29ABCDE1234F1Z5"
                      pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}"
                      maxLength="15"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    Verify GST
                  </button>
                </form>
              )}
            </div>
          )}

          {/* UPI Verification */}
          <div className="settings-card">
            <div className="card-header">
              <FaMoneyCheckAlt />
              <h2>UPI Verification</h2>
              {user?.upiVerification?.verified && (
                <span className="verified-badge"><FaCheckCircle /> Verified</span>
              )}
              {user?.upiVerification?.pending && !user?.upiVerification?.verified && (
                <span className="pending-badge"><FaTimesCircle /> Pending</span>
              )}
            </div>
            {user?.upiVerification?.verified ? (
              <div className="verified-info">
                <p>UPI ID: {user.upiVerification.upiId}</p>
                <p className="verified-date">
                  Verified on {new Date(user.upiVerification.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            ) : user?.upiVerification?.pending ? (
              <div className="pending-info">
                <p>UPI ID: {user.upiVerification.upiId}</p>
                <p className="pending-date">
                  Submitted on {new Date(user.upiVerification.submittedAt).toLocaleDateString()}
                </p>
                <p className="info-text">Your UPI verification is under review. This usually takes 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleUPIVerification}>
                <div className="form-group">
                  <label>UPI ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="yourname@upi"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Verify UPI
                </button>
              </form>
            )}
          </div>

          {/* Driving License Verification */}
          {user?.userType === 'delivery_partner' && (
            <div className="settings-card">
              <div className="card-header">
                <FaCar />
                <h2>Driving License</h2>
                {user?.govtIdVerification?.drivingLicense?.verified && (
                  <span className="verified-badge"><FaCheckCircle /> Verified</span>
                )}
                {user?.govtIdVerification?.drivingLicense?.pending && !user?.govtIdVerification?.drivingLicense?.verified && (
                  <span className="pending-badge"><FaTimesCircle /> Pending</span>
                )}
              </div>
              {user?.govtIdVerification?.drivingLicense?.verified ? (
                <div className="verified-info">
                  <p>License No: {user.govtIdVerification.drivingLicense.number}</p>
                  <p className="verified-date">
                    Verified on {new Date(user.govtIdVerification.drivingLicense.verifiedAt).toLocaleDateString()}
                  </p>
                </div>
              ) : user?.govtIdVerification?.drivingLicense?.pending ? (
                <div className="pending-info">
                  <p>License No: {user.govtIdVerification.drivingLicense.number}</p>
                  <p className="pending-date">
                    Submitted on {new Date(user.govtIdVerification.drivingLicense.submittedAt).toLocaleDateString()}
                  </p>
                  <p className="info-text">Your Driving License verification is under review. This usually takes 24-48 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleLicenseVerification}>
                  <div className="form-group">
                    <label>License Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={licenseData.licenseNumber}
                      onChange={(e) => setLicenseData({ ...licenseData, licenseNumber: e.target.value })}
                      placeholder="DL-1420110012345"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      className="form-control"
                      value={licenseData.dateOfBirth}
                      onChange={(e) => setLicenseData({ ...licenseData, dateOfBirth: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    Verify License
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Delete Account */}
          <div className="settings-card danger-zone">
            <div className="card-header">
              <FaTrash />
              <h2>Delete Account</h2>
            </div>
            <p className="danger-warning">
              <FaTimesCircle /> Warning: This action is irreversible. All your data will be permanently deleted.
            </p>
            <form onSubmit={handleDeleteAccount}>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={deleteData.password}
                  onChange={(e) => setDeleteData({ ...deleteData, password: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Type "DELETE" to confirm</label>
                <input
                  type="text"
                  className="form-control"
                  value={deleteData.confirmation}
                  onChange={(e) => setDeleteData({ ...deleteData, confirmation: e.target.value })}
                  placeholder="DELETE"
                  required
                />
              </div>
              <button type="submit" className="btn btn-danger" disabled={loading}>
                Delete My Account
              </button>
            </form>
          </div>
        </div>
        )}

        {/* Address Book Tab */}
        {activeTab === 'addresses' && (
          <AddressBook />
        )}
      </div>
    </Layout>
  );
};

export default AccountSettings;
